import * as microsoftTeams from "@microsoft/teams-js";
import { PublicClientApplication, AuthenticationScheme } from "@azure/msal-browser";
import * as Config from './config';
import { fetchServerSideToken } from '../token/tokenUtils';

class TeamsAuthService {
    private config: Config.ConfigEntry;
    private teamsContext?: microsoftTeams.app.Context;
    private authenticationPromise: Promise<string> | null = null;
    private readonly scopes: string[];
    private pca: PublicClientApplication;

    public get currentUserName(): string | undefined {
        return this.teamsContext?.user?.displayName;
    }

    public get currentUserId(): string | undefined {
        return this.teamsContext?.user?.id;
    }

    public get currentUserPrincipalName(): string | undefined {
        return this.teamsContext?.user?.userPrincipalName;
    }

    constructor() {
        this.config = Config.environment[window.location.host] || Config.environment['localhost:3000'];
        this.scopes = this.config.scopes;

        const msalConfig = {
            auth: {
                clientId: this.config.clientId,
                authority: Config.authority,
                redirectUri: this.config.redirectUri
            },
            cache: {
                cacheLocation: "localStorage",
                storeAuthStateInCookie: false
            }
        };

        this.pca = new PublicClientApplication(msalConfig);
    }

    public setContext(ctx: microsoftTeams.app.Context): void {
        this.teamsContext = ctx;
    }

    async init(): Promise<boolean> {
        try {
            await microsoftTeams.app.initialize();
            this.teamsContext = await microsoftTeams.app.getContext();
            return true;
        } catch (error) {
            console.error('Impossibile inizializzare Teams:', error);
            return false;
        }
    }

    public isLoggedIn(): boolean {
        return !!this.teamsContext?.user?.id;
    }

    public async getAccessToken(): Promise<string> {
        if (this.authenticationPromise) {
            return this.authenticationPromise;
        }
        if (!microsoftTeams.authentication.getAuthToken) {
            throw new Error('getAuthToken non è supportato in questa versione di Teams SDK');
        }

        try {
            this.authenticationPromise = this.performAuthentication();
            return await this.authenticationPromise.finally(() => {
                this.authenticationPromise = null;
            });
        } catch (error) {
            console.error('Impossibile ottenere il token di accesso:', error);
            throw error;
        }
    }

    private async performAuthentication(): Promise<string> {
        try {
            const teamsToken = await microsoftTeams.authentication.getAuthToken({
                resources: this.scopes,
                silent: true
            });
    
            return await this.acquireFeTokenWithHint(teamsToken);
        } catch (error) {
            // Tentativo interattivo solo se siamo in Teams
            if (!window.navigator.userAgent.includes('MSAppHost')) {
                throw new Error("Non siamo in Teams, impossibile tentare login interattivo");
            }

            return this.getAccessTokenInteractive();
        }
    }

    private async getAccessTokenInteractive(): Promise<string> {
        try {
            const teamsToken = await microsoftTeams.authentication.getAuthToken({
                resources: this.scopes,
                silent: false
            });

            return await this.acquireFeTokenWithHint(teamsToken);
        } catch (error) {
            console.error('Autenticazione interattiva fallita:', error);
            throw new Error('Impossibile acquisire token interattivamente');
        }
    }

    private async acquireFeTokenWithHint(idTokenHint: string): Promise<string> {
        const request = {
            scopes: this.config.be_scopes,
            authenticationScheme: AuthenticationScheme.BEARER,
            extraQueryParameters: { id_token_hint: idTokenHint },
            prompt: "none" as const
        };

        try {
            const response = await this.pca.ssoSilent(request);
            const account = response.account;

            if (!account) {
                throw new Error("Nessun account disponibile dopo ssoSilent");
            }

            const tokenResponse = await this.pca.acquireTokenSilent({
                ...request,
                account
            });

            return tokenResponse.accessToken;
        } catch (error) {
            console.error("Errore durante l'acquisizione del token FE:", error);
            throw error;
        }
    }

    public async tryGetAccessToken(): Promise<[boolean, string | Error]> {
        try {
            const token = await this.getAccessToken();

            return [true, token];
        } catch (error) {
            return [false, error instanceof Error ? error : new Error('Si è verificato un errore sconosciuto')];
        }
    }

    public async logout(): Promise<void> {
        try {
            localStorage.clear();
            this.teamsContext = undefined;
            this.authenticationPromise = null;
        } catch (error) {
            console.error('Logout fallito:', error);
            throw new Error('Impossibile effettuare il logout');
        }
    }

    public async fetchServerSideToken(clientSideToken: string): Promise<string> {
        return fetchServerSideToken(clientSideToken, this.config.be_scopes[0]);
    }
}

export default new TeamsAuthService();